import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { KeyValueEditor } from './KeyValueEditor';
import { HTTP_METHODS, AUTH_TYPES, CONTENT_TYPES, CATEGORIES } from './constants';
import { Wrench, Save, Play, Loader2, FileJson, Zap } from 'lucide-react';

export const ToolFormDialog = ({
  open,
  onOpenChange,
  editingTool,
  formData,
  setFormData,
  bodyJsonString,
  setBodyJsonString,
  testPayloadString,
  setTestPayloadString,
  formLoading,
  testLoading,
  onSubmit,
  onTest,
  onCancel
}) => {
  const updateAuth = (field, value) => {
    setFormData({
      ...formData,
      auth: { ...formData.auth, [field]: value }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-purple-600" />
            {editingTool ? 'Editar Herramienta' : 'Nueva Herramienta'}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-4">
              <TabsTrigger value="basic">Básico</TabsTrigger>
              <TabsTrigger value="request">Request</TabsTrigger>
              <TabsTrigger value="auth">Auth</TabsTrigger>
              <TabsTrigger value="body">Body</TabsTrigger>
              <TabsTrigger value="advanced">Avanzado</TabsTrigger>
            </TabsList>

            {/* Basic Tab */}
            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ej: Enviar WhatsApp"
                    data-testid="tool-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.code} value={cat.code}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe qué hace esta herramienta..."
                  rows={2}
                />
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Método HTTP *</Label>
                  <Select value={formData.method} onValueChange={(v) => setFormData({...formData, method: v})}>
                    <SelectTrigger data-testid="tool-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HTTP_METHODS.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3 space-y-2">
                  <Label>URL Base *</Label>
                  <Input
                    value={formData.base_url}
                    onChange={(e) => setFormData({...formData, base_url: e.target.value})}
                    placeholder="https://api.example.com"
                    data-testid="tool-base-url"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Endpoint (Ruta)</Label>
                <Input
                  value={formData.endpoint}
                  onChange={(e) => setFormData({...formData, endpoint: e.target.value})}
                  placeholder="/v1/messages/send"
                />
                <p className="text-xs text-gray-500">
                  Usa {'{{variable}}'} para variables dinámicas. Ej: /users/{'{'}{'{'}'id{'}'}{'}'}/orders
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activo</SelectItem>
                      <SelectItem value="inactive">Inactivo</SelectItem>
                      <SelectItem value="draft">Borrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Content-Type</Label>
                  <Select value={formData.content_type} onValueChange={(v) => setFormData({...formData, content_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Request Tab */}
            <TabsContent value="request" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label className="mb-2 block">Headers</Label>
                  <KeyValueEditor
                    items={formData.headers}
                    onChange={(items) => setFormData({...formData, headers: items})}
                    secretField={true}
                    placeholder={{ key: 'Header Name', value: 'Header Value' }}
                  />
                </div>
                
                <Separator />
                
                <div>
                  <Label className="mb-2 block">Query Parameters</Label>
                  <KeyValueEditor
                    items={formData.query_params}
                    onChange={(items) => setFormData({...formData, query_params: items})}
                    placeholder={{ key: 'Param Name', value: 'Param Value' }}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Auth Tab */}
            <TabsContent value="auth" className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de Autenticación</Label>
                <Select value={formData.auth.type} onValueChange={(v) => updateAuth('type', v)}>
                  <SelectTrigger data-testid="auth-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUTH_TYPES.map(at => (
                      <SelectItem key={at.value} value={at.value}>{at.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.auth.type === 'bearer_token' && (
                <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                  <Label>Bearer Token</Label>
                  <Input
                    type="password"
                    value={formData.auth.bearer_token}
                    onChange={(e) => updateAuth('bearer_token', e.target.value)}
                    placeholder="Tu token de acceso"
                  />
                </div>
              )}
              
              {formData.auth.type === 'api_key_header' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Nombre del Header</Label>
                    <Input
                      value={formData.auth.api_key_name}
                      onChange={(e) => updateAuth('api_key_name', e.target.value)}
                      placeholder="X-API-Key"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      value={formData.auth.api_key_value}
                      onChange={(e) => updateAuth('api_key_value', e.target.value)}
                      placeholder="Tu API key"
                    />
                  </div>
                </div>
              )}
              
              {formData.auth.type === 'api_key_query' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Nombre del Parámetro</Label>
                    <Input
                      value={formData.auth.api_key_name}
                      onChange={(e) => updateAuth('api_key_name', e.target.value)}
                      placeholder="api_key"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      value={formData.auth.api_key_value}
                      onChange={(e) => updateAuth('api_key_value', e.target.value)}
                      placeholder="Tu API key"
                    />
                  </div>
                </div>
              )}
              
              {formData.auth.type === 'basic_auth' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Usuario</Label>
                    <Input
                      value={formData.auth.basic_username}
                      onChange={(e) => updateAuth('basic_username', e.target.value)}
                      placeholder="username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contraseña</Label>
                    <Input
                      type="password"
                      value={formData.auth.basic_password}
                      onChange={(e) => updateAuth('basic_password', e.target.value)}
                      placeholder="password"
                    />
                  </div>
                </div>
              )}
              
              {formData.auth.type === 'custom_header' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Nombre del Header</Label>
                    <Input
                      value={formData.auth.custom_header_name}
                      onChange={(e) => updateAuth('custom_header_name', e.target.value)}
                      placeholder="X-Custom-Auth"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Valor</Label>
                    <Input
                      type="password"
                      value={formData.auth.custom_header_value}
                      onChange={(e) => updateAuth('custom_header_value', e.target.value)}
                      placeholder="valor-secreto"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Body Tab */}
            <TabsContent value="body" className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de Body</Label>
                <Select value={formData.body_type} onValueChange={(v) => setFormData({...formData, body_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin Body</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="form">Form Data</SelectItem>
                    <SelectItem value="raw">Raw</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.body_type === 'json' && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileJson className="h-4 w-4" />
                    Body JSON
                  </Label>
                  <Textarea
                    value={bodyJsonString}
                    onChange={(e) => setBodyJsonString(e.target.value)}
                    placeholder='{"key": "value"}'
                    rows={10}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    Usa {'{{variable}}'} para valores dinámicos. Ej: {'"phone": "{{customer_phone}}"'}
                  </p>
                </div>
              )}
              
              {formData.body_type === 'form' && (
                <div>
                  <Label className="mb-2 block">Form Fields</Label>
                  <KeyValueEditor
                    items={formData.body_form}
                    onChange={(items) => setFormData({...formData, body_form: items})}
                    placeholder={{ key: 'Field Name', value: 'Field Value' }}
                  />
                </div>
              )}
              
              {formData.body_type === 'raw' && (
                <div className="space-y-2">
                  <Label>Raw Body</Label>
                  <Textarea
                    value={formData.body_raw}
                    onChange={(e) => setFormData({...formData, body_raw: e.target.value})}
                    placeholder="Contenido raw..."
                    rows={10}
                    className="font-mono text-sm"
                  />
                </div>
              )}
            </TabsContent>

            {/* Advanced Tab */}
            <TabsContent value="advanced" className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Timeout (segundos)</Label>
                  <Input
                    type="number"
                    value={formData.timeout_seconds}
                    onChange={(e) => setFormData({...formData, timeout_seconds: parseInt(e.target.value) || 30})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reintentos</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    value={formData.retry_count}
                    onChange={(e) => setFormData({...formData, retry_count: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Respuesta</Label>
                  <Select value={formData.response_type} onValueChange={(v) => setFormData({...formData, response_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="binary">Binary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.follow_redirects}
                    onCheckedChange={(c) => setFormData({...formData, follow_redirects: c})}
                  />
                  <Label>Seguir Redirects</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.verify_ssl}
                    onCheckedChange={(c) => setFormData({...formData, verify_ssl: c})}
                  />
                  <Label>Verificar SSL</Label>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label>Códigos de Estado Esperados</Label>
                <Input
                  value={formData.expected_status_codes.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData, 
                    expected_status_codes: e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
                  })}
                  placeholder="200, 201, 204"
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Test Payload (Variables de Prueba)
                </Label>
                <Textarea
                  value={testPayloadString}
                  onChange={(e) => setTestPayloadString(e.target.value)}
                  placeholder='{"customer_phone": "+573001234567"}'
                  rows={4}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500">
                  Variables que se usarán al probar la herramienta
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </ScrollArea>
        
        <DialogFooter className="border-t pt-4">
          <div className="flex items-center gap-2 w-full justify-between">
            <div>
              {editingTool && (
                <Button
                  variant="outline"
                  onClick={onTest}
                  disabled={testLoading}
                >
                  {testLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                  Probar
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onCancel} disabled={formLoading}>
                Cancelar
              </Button>
              <Button onClick={onSubmit} disabled={formLoading} data-testid="save-tool-btn">
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {editingTool ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ToolFormDialog;
